import React from 'react';
import type { Toast as ToastType } from './types';
import { STATUS_LABELS, STATUS_COLORS } from '@/types/api/quickTrade';
import { CHAINS } from '@/config/chains';
import { TokenIcon } from '@/components/common/TokenIcon/TokenIcon';

interface ToastProps {
  toast: ToastType;
  onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({ toast, onClose }) => {
  if (toast.kind === 'generic') {
    const variantClassName =
      toast.variant === 'success'
        ? 'border-green-700/50'
        : toast.variant === 'error'
          ? 'border-red-700/50'
          : 'border-gray-700';

    const title =
      toast.title ||
      (toast.variant === 'success' ? 'Success' : toast.variant === 'error' ? 'Error' : 'Notice');

    const handleToastClick = () => {
      if (toast.onClick) {
        toast.onClick();
        onClose();
      }
    };

    return (
      <div
        className={`rounded-lg border bg-gray-900 text-sm text-gray-200 shadow-lg ${variantClassName} ${toast.onClick ? 'cursor-pointer hover:bg-gray-800' : ''}`}
        onClick={toast.onClick ? handleToastClick : undefined}
      >
        <div className="flex items-start justify-between px-4 pt-4">
          <div>
            <div className="font-semibold text-white">{title}</div>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-xl leading-none text-gray-400 hover:text-gray-200"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="px-4 pb-4">
          <div className="mt-1 text-xs text-gray-300">{toast.message || 'Operation completed.'}</div>
          {toast.actionLabel && toast.onClick && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toast.onClick?.();
                onClose();
              }}
              className="mt-3 text-xs font-semibold text-blue-400 hover:text-blue-300"
            >
              {toast.actionLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  const {
    tradeId,
    tokenSymbol,
    tokenAddress,
    tokenLogo,
    tradeType = 'buy',
    status = 'queued',
    message,
    txHash,
    chainId,
    strategyName,
  } = toast;

  const statusLabel = STATUS_LABELS[status] || status;
  const statusColor = STATUS_COLORS[status] || STATUS_COLORS.queued;
  const chainConfig = chainId ? CHAINS.find((c) => c.numericId === chainId) : undefined;
  const tokenExplorerUrl = tokenAddress && chainConfig
    ? `/data-explorer/${chainConfig.id}/token/${tokenAddress}`
    : undefined;
  const explorerUrl = txHash
    ? `${chainConfig?.explorerUrl || 'https://etherscan.io'}/tx/${txHash}`
    : undefined;
  const explorerName = chainConfig?.explorerUrl
    ? new URL(chainConfig.explorerUrl).hostname.replace('www.', '')
    : 'Etherscan';

  const displayName = tokenSymbol || tokenAddress?.slice(0, 6) || 'Token';

  const defaultMessage =
    status === 'success'
      ? 'Trade executed successfully.'
      : status === 'failed' || status === 'error'
        ? 'Trade failed.'
        : 'Processing trade…';

  return (
    <div className="bg-gray-900 dark:bg-gray-800 border border-gray-800 dark:border-gray-700 rounded-lg shadow-lg text-sm text-gray-200 dark:text-gray-300 w-72 sm:w-80 animate-slide-in">
      {/* Header */}
      <div className="flex items-start gap-3 px-4 pt-4">
        <TokenIcon
          symbol={tokenSymbol || tokenAddress?.slice(0, 4) || '?'}
          imageUrl={tokenLogo}
          size="lg"
          className="mt-0.5"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className={`inline-block rounded px-1 py-0.5 text-[10px] font-bold uppercase leading-none ${tradeType === 'sell' ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
              {tradeType === 'sell' ? 'Sell' : 'Buy'}
            </span>
            {chainConfig && (
              <span className="inline-block rounded bg-gray-700/60 px-1 py-0.5 text-[10px] font-medium uppercase leading-none text-gray-400">
                {chainConfig.shortName}
              </span>
            )}
          </div>
          <div className="mt-1 font-semibold text-white dark:text-gray-100 truncate">
            {tokenExplorerUrl ? (
              <a
                href={tokenExplorerUrl}
                className="hover:text-indigo-300"
              >
                {displayName}
              </a>
            ) : (
              displayName
            )}
          </div>
          {strategyName && (
            <div className="text-xs text-gray-400 dark:text-gray-500 truncate">
              {strategyName}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-200 dark:text-gray-500 dark:hover:text-gray-300 text-lg leading-none shrink-0"
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Body */}
      <div className="px-4 pb-3 pt-2">
        <div className="flex items-start gap-2">
          <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[11px] font-semibold ${statusColor}`}>
            {statusLabel}
          </span>
          <span className="min-w-0 break-words text-xs text-gray-400 dark:text-gray-500">
            {message || defaultMessage}
          </span>
        </div>

        {/* Footer */}
        <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-600">
          <span>#{tradeId ?? '-'}</span>
          {explorerUrl && tokenAddress && (
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-400 hover:text-indigo-200 dark:text-indigo-500 dark:hover:text-indigo-300"
            >
              {status === 'success' ? `View on ${explorerName}` : 'View tx'}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
